/**
 * Plug-in retirement readiness — READ-ONLY, flag-gated pre-flight card (Phase 4 prep).
 *
 * Sources TWO read-only backend planners so the owner can see plug-in retirement
 * readiness before Phase 4 goes live:
 *  - GET /job-connections/queue-migration      (flag SAIK_QUEUE_MIGRATION_ENABLED)
 *  - GET /job-connections/extension-retirement (flag SAIK_EXTENSION_RETIREMENT_ENABLED)
 *
 * Both endpoints are flag-gated and answer {enabled:false} while their flag is
 * OFF, so this card renders NOTHING unless at least one reports enabled:true —
 * it is INERT in production (both flags default OFF). It also renders nothing
 * while loading, on request error/timeout, or when neither returns data.
 *
 * When at least one is enabled:true it shows ONE card with two buckets:
 *  - applications to requeue in-app (queue-migration summary.requeue_in_app,
 *    plus the blocked sub-count)
 *  - extension tokens to revoke (extension-retirement counts.revoke /
 *    counts.leave)
 *
 * Display-only: no control here acts, and the only network is the two GETs.
 * Only aggregate counts are shown — never a token_prefix, hash or secret.
 */
import { useEffect, useState } from 'react';
import { Archive } from 'lucide-react';
import Card from './Card';
import { jobbotApi, type ExtensionRetirementCounts, type QueueMigrationSummary } from '../api/jobbot';

/** Keep a slow/dead backend from ever leaving a stray placeholder on screen. */
const REQUEST_TIMEOUT_MS = 8000;

export default function PluginRetirementCard() {
  const [migration, setMigration] = useState<QueueMigrationSummary | null>(null);
  const [retirement, setRetirement] = useState<ExtensionRetirementCounts | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    (async () => {
      const [mig, ret] = await Promise.allSettled([
        jobbotApi.extension.queueMigration({ signal: controller.signal }),
        jobbotApi.extension.extensionRetirement({ signal: controller.signal }),
      ]);
      window.clearTimeout(timer);
      if (!alive) return;
      // Each planner contributes only when its own flag reported enabled:true.
      const m =
        mig.status === 'fulfilled' && mig.value?.data?.enabled
          ? mig.value.data.summary ?? null
          : null;
      const r =
        ret.status === 'fulfilled' && ret.value?.data?.enabled
          ? ret.value.data.counts ?? null
          : null;
      setMigration(m);
      setRetirement(r);
      setLoading(false);
    })();
    return () => {
      alive = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, []);

  if (loading) return null; // never flash a placeholder before we know the flags
  if (!migration && !retirement) return null; // both OFF / error / timeout → inert

  return (
    <Card className="mb-6 p-4" data-agent-id="jobbot.sites.plugin-retirement">
      <div className="mb-3 flex items-center gap-2">
        <Archive size={18} className="text-brand-600" aria-hidden="true" />
        <h2 className="font-semibold text-slate-950 dark:text-slate-100">Plug-in retirement readiness</h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div
          className="rounded-lg border border-slate-200 dark:border-slate-700 p-3"
          data-agent-id="jobbot.sites.plugin-retirement.requeue"
        >
          <p className="text-2xl font-semibold text-brand-700 dark:text-brand-400">
            {migration?.requeue_in_app ?? 0}
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">applications to requeue in-app</p>
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
            {migration?.blocked ?? 0} blocked
          </p>
        </div>
        <div
          className="rounded-lg border border-slate-200 dark:border-slate-700 p-3"
          data-agent-id="jobbot.sites.plugin-retirement.revoke"
        >
          <p className="text-2xl font-semibold text-amber-700 dark:text-amber-400">
            {retirement?.revoke ?? 0}
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">retired plug-in tokens to revoke</p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-500">
            {retirement?.leave ?? 0} left as-is
          </p>
        </div>
      </div>
    </Card>
  );
}
